import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { ALWAYS_PREMIUM } from "../config/featureFlags";

export const PLAN_LIMITS = {
    free: { 
        dailyGenerations: 1,
        maxClothes: 20 // Maximum 20 vêtements pour le plan gratuit
    },
    premium: { 
        dailyGenerations: null, // null -> illimité
        maxClothes: null // null -> illimité
    }
};

const DEFAULT_PROFILE = ALWAYS_PREMIUM
    ? {
        plan: "premium",
        subscriptionStatus: "premium",
        limits: { ...PLAN_LIMITS.premium },
      }
    : {
        plan: "free",
        subscriptionStatus: "free",
        limits: { ...PLAN_LIMITS.free },
      };

/**
 * Ensure that the user document exists and contains the required baseline fields.
 * Returns the merged profile (existing data + defaults + overrides).
 */
export async function ensureUserProfile(user, overrides = {}) {
    if (!user?.uid) {
        return null;
    }

    const userDocRef = doc(db, "users", user.uid);
    const snap = await getDoc(userDocRef);

    const baseProfile = {
        email: user.email || null,
        username: user.displayName || user.email?.split("@")[0] || "Utilisateur",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...DEFAULT_PROFILE,
        limits: { ...DEFAULT_PROFILE.limits },
        ...overrides,
    };

    if (!snap.exists()) {
        await setDoc(userDocRef, baseProfile, { merge: true });
        return baseProfile;
    }

    const currentData = snap.data() || {};
    const updates = {};
    
    // SÉCURITÉ : Ne jamais écraser le plan existant
    // Créer un baseline sans plan/subscriptionStatus pour ne pas les écraser
    const { plan, subscriptionStatus, limits, ...baselineWithoutPlan } = DEFAULT_PROFILE;
    
    // Inject missing baseline fields without overriding existing custom data.
    // IMPORTANT : Ne jamais toucher au plan, subscriptionStatus, ou limits si déjà défini
    Object.entries(baselineWithoutPlan).forEach(([key, value]) => {
        if (currentData[key] === undefined) {
            updates[key] = value;
        }
    });
    
    // Seulement ajouter plan/subscriptionStatus si complètement absent (nouveau compte)
    if (currentData.plan === undefined && currentData.subscriptionStatus === undefined) {
        updates.plan = DEFAULT_PROFILE.plan;
        updates.subscriptionStatus = DEFAULT_PROFILE.subscriptionStatus;
        updates.limits = { ...DEFAULT_PROFILE.limits };
    } else if (currentData.limits === undefined) {
        // Si plan existe mais pas limits, ajouter les limites correspondantes
        const planType = currentData.plan || 'free';
        updates.limits = { ...PLAN_LIMITS[planType] || PLAN_LIMITS.free };
    }

    if (!currentData.username && baseProfile.username) {
        updates.username = baseProfile.username;
    }

    if (!currentData.email && baseProfile.email) {
        updates.email = baseProfile.email;
    }

    let mergedData = { ...currentData };

    if (Object.keys(updates).length > 0) {
        updates.updatedAt = serverTimestamp();
        await setDoc(userDocRef, updates, { merge: true });
        mergedData = { ...mergedData, ...updates };
    }

    // SUPPRIMÉ : Ne jamais forcer le premium, même si ALWAYS_PREMIUM est activé
    // Le plan doit être défini uniquement lors de la création du compte ou via paiement

    // Traiter les overrides mais PROTÉGER le plan existant
    if (Object.keys(overrides).length > 0) {
        // SÉCURITÉ : Ne jamais permettre aux overrides d'écraser le plan existant vers premium
        // sauf si l'utilisateur a un abonnement Stripe actif
        const safeOverrides = { ...overrides };
        
        // Si le plan actuel est "free" et qu'on essaie de le mettre en "premium" via overrides,
        // vérifier qu'il y a un abonnement Stripe actif
        if (currentData.plan === 'free' && safeOverrides.plan === 'premium') {
            const hasStripeSubscription = currentData.stripeSubscriptionId;
            if (!hasStripeSubscription || hasStripeSubscription.trim() === '') {
                console.warn('⚠️ BLOCKED: Attempted to upgrade to premium without Stripe subscription. Ignoring plan override.');
                delete safeOverrides.plan;
                delete safeOverrides.subscriptionStatus;
                // Garder les autres overrides (comme limits si nécessaire)
            }
        }
        
        const overridePayload = { ...safeOverrides, updatedAt: serverTimestamp() };
        if (Object.keys(overridePayload).length > 1) { // Plus que juste updatedAt
            await setDoc(userDocRef, overridePayload, { merge: true });
            mergedData = { ...mergedData, ...safeOverrides };
        }
    }

    return mergedData;
}

export function getPlanLimits(plan) {
    return PLAN_LIMITS[plan] || PLAN_LIMITS.free;
}
